import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import {
  getBlogCategories,
  getBlogs,
} from "../../store/actions/councilActions/blogAction";
import BreadCrumb from "../BreadCrumb";
import "./blog.css";

/*
  Blogs on the website, category wise.

  /blogs                 everything
  /blogs/<category slug> one category

  The category is in the address rather than only in state, so a category page
  can be linked to and shared.
*/
const dmy = (value) => (value ? moment(value).format("DD MMM YYYY") : "");

const Blogs = () => {
  const dispatch = useDispatch();
  const { category } = useParams();

  const [posts, setPosts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  // Daily, weekly or monthly. "" is everything, which is the default.
  const [frequency, setFrequency] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(getBlogCategories(true)).then(setCategories);
  }, [dispatch]);

  // A different category is a different listing, so start at page one.
  useEffect(() => {
    setPage(1);
  }, [category]);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await dispatch(
      getBlogs({ active: 1, category: category || "", frequency, search, page })
    );
    setPosts(data || null);
    setLoading(false);
  }, [dispatch, category, frequency, search, page]);

  useEffect(() => {
    load();
  }, [load]);

  const current = categories.find((c) => c.slug === category);

  const breadcrumbs = [
    { path: "/", label: "Home" },
    ...(current
      ? [{ path: "/blogs", label: "Blogs" }, { label: current.name }]
      : [{ label: "Blogs" }]),
  ];

  const rows = posts?.data || [];
  const lastPage = Number(posts?.last_page || 1);

  return (
    <div class="lists-box">
      <div class="container">
        <BreadCrumb crumbs={breadcrumbs} />
        <div class="title-box">
          <h4>{current ? current.name : "Blogs"}</h4>
        </div>

        <div class="row">
          {/* ---- the posts ---- */}
          <div class="col-md-9">
            {loading ? (
              <div class="text-center py-5">
                <Spinner animation="border" role="status" size="lg">
                  <span class="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : rows.length === 0 ? (
              <p class="py-4">
                {search
                  ? "Nothing matches that search."
                  : "No blog has been published here yet."}
              </p>
            ) : (
              <div class="row">
                {rows.map((post) => (
                  <div class="col-md-6 mb-4" key={post.id}>
                    <div class="blog-card">
                      <Link to={`/blog/${post.frequency || "post"}/${post.slug || post.id}`}>
                        <div class="blog-card-img">
                          <img
                            src={post.file || "/images/logo.png"}
                            alt={post.title}
                          />
                        </div>
                      </Link>
                      <div class="blog-card-body">
                        {post.category?.name && (
                          <Link
                            class="blog-chip"
                            to={`/blogs/${post.category.slug}`}
                          >
                            {post.category.name}
                          </Link>
                        )}
                        <h5>
                          <Link to={`/blog/${post.frequency || "post"}/${post.slug || post.id}`}>
                            {post.title}
                          </Link>
                        </h5>
                        <div class="blog-meta">
                          {dmy(post.published_at)}
                          {post.author ? ` · ${post.author}` : ""}
                          {post.frequency ? ` · ${post.frequency}` : ""}
                          {post.video_url ? " · ▶ Video" : ""}
                          {` · ${Number(post.views) || 0} views`}
                        </div>
                        {post.short_description && (
                          <p>{post.short_description}</p>
                        )}
                        <div class="blog-actions">
                          <Link
                            class="blog-more"
                            to={`/blog/${post.frequency || "post"}/${post.slug || post.id}`}
                          >
                            Read more →
                          </Link>
                          {/* the website's own contact page, so a reader can
                              ask about what they have just read */}
                          <Link class="blog-contact" to="/contact-us">
                            Contact Us
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {lastPage > 1 && (
              <div class="d-flex justify-content-center my-4">
                <button
                  class="btn btn-comman mr-2"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                <span class="align-self-center mx-2">
                  Page {page} of {lastPage}
                </span>
                <button
                  class="btn btn-comman ml-2"
                  disabled={page >= lastPage}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* ---- categories ---- */}
          <div class="col-md-3">
            <div class="blog-side">
              <input
                type="text"
                class="form-control mb-3"
                placeholder="Search blogs"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />

              {/* daily / weekly / monthly, as set by the council */}
              <h6>Blog Type</h6>
              <ul class="blog-cats">
                {[
                  { key: "", label: "All" },
                  { key: "daily", label: "Daily Blog" },
                  { key: "weekly", label: "Weekly Blog" },
                  { key: "monthly", label: "Monthly Blog" },
                ].map((f) => (
                  <li key={f.key || "all"}>
                    <a
                      href="#/"
                      class={frequency === f.key ? "active" : ""}
                      onClick={(e) => {
                        e.preventDefault();
                        setFrequency(f.key);
                        setPage(1);
                      }}
                    >
                      {f.label}
                    </a>
                  </li>
                ))}
              </ul>

              <h6>Categories</h6>
              <ul class="blog-cats">
                <li>
                  <Link class={!category ? "active" : ""} to="/blogs">
                    All
                  </Link>
                </li>
                {categories
                  .filter((c) => c.blogs_count > 0)
                  .map((c) => (
                    <li key={c.id}>
                      <Link
                        class={category === c.slug ? "active" : ""}
                        to={`/blogs/${c.slug}`}
                      >
                        {c.name} <span>({c.blogs_count})</span>
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blogs;
